import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  createPlanRequest,
  getPlanById,
  getPlans,
  removePlan,
  updatePlanRequest,
} from "../../../services/api/plansFeatures/plansApi";

const rejectApiResponse = (responseData, fallbackMessage) => {
  if (responseData?.success === false) {
    return responseData.message ?? fallbackMessage;
  }
  return null;
};

const PLAN_CREATE_ERROR_FALLBACK = {
  en: "Failed to create plan.",
  ar: "فشل في إنشاء الخطة.",
};

const PLAN_UPDATE_ERROR_FALLBACK = {
  en: "Failed to update plan.",
  ar: "فشل في تحديث الخطة.",
};

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

export const fetchPlansData = createAsyncThunk(
  "plans/fetchPlansData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPlans();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch plans data");
    }
  },
);

export const addPlan = createAsyncThunk(
  "plans/addPlan",
  async (planData, { rejectWithValue }) => {
    try {
      const response = await createPlanRequest(planData);

      const apiError = rejectApiResponse(response.data, PLAN_CREATE_ERROR_FALLBACK);
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        plan: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, PLAN_CREATE_ERROR_FALLBACK),
      );
    }
  },
);

export const fetchPlanById = createAsyncThunk(
  "plans/fetchPlanById",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await getPlanById(planId);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch plan data");
    }
  },
);

export const updatePlan = createAsyncThunk(
  "plans/updatePlan",
  async ({ planId, planData }, { rejectWithValue }) => {
    try {
      const response = await updatePlanRequest(planId, planData);

      const apiError = rejectApiResponse(response.data, PLAN_UPDATE_ERROR_FALLBACK);
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        plan: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, PLAN_UPDATE_ERROR_FALLBACK),
      );
    }
  },
);

export const deletePlan = createAsyncThunk(
  "plans/deletePlan",
  async (planId, { rejectWithValue }) => {
    try {
      await removePlan(planId);
      return planId;
    } catch (error) {
      return rejectWithValue("Failed to delete plan");
    }
  },
);

const plansSlice = createSlice({
  name: "plans",
  initialState: {
    plansData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlansData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlansData.fulfilled, (state, action) => {
        state.loading = false;
        state.plansData = action.payload;
      })
      .addCase(fetchPlansData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPlan.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.plan) {
          state.plansData.push(action.payload.plan);
        }
      })
      .addCase(addPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        state.loading = false;
        const plan = action.payload?.plan;
        if (!plan) return;

        const index = state.plansData.findIndex((item) => item.id === plan.id);
        if (index !== -1) {
          state.plansData[index] = plan;
        }
      })
      .addCase(updatePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deletePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.plansData = state.plansData.filter(
          (plan) => plan.id !== action.payload,
        );
        const toastMessages = {
          en: "Plan deleted successfully!",
          ar: "تم حذف الخطة بنجاح!",
        };
        toast.success(toastMessages[getLang()]);
      })
      .addCase(deletePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        const toastMessages = {
          en: "Failed to delete plan.",
          ar: "فشل في حذف الخطة.",
        };
        toast.error(toastMessages[getLang()]);
      })
      .addCase(fetchPlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.plan = action.payload;
      })
      .addCase(fetchPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default plansSlice.reducer;
